/**
 * Degree Breakdown Table with Interactive Pie Chart
 * Wrapper around InteractivePieChartCard for degree distribution
 * Includes comparison columns: "Total Sistema" and "% Cred." vs active ministers
 */

import { useMemo } from "react";
import { InteractivePieChartCard } from "@/components/organisms/InteractivePieChartCard";
import type { MinisterInRequest } from "../types";
import type { MinisterSystemDegreeCount } from "../hooks/useMinistersListReport";

interface DegreeBreakdownTableProps {
  ministers: MinisterInRequest[];
  isLoading?: boolean;
  /** System-wide active minister counts per degree for comparison */
  systemDegreeCounts?: MinisterSystemDegreeCount[];
}

export function DegreeBreakdownTable({
  ministers,
  isLoading,
  systemDegreeCounts = [],
}: DegreeBreakdownTableProps) {
  // Count ministers in requests grouped by degree name
  const data = useMemo(() => {
    const degreeCount = new Map<string, number>();
    ministers.forEach((m) => {
      const degree = m.degree_name || "Sin Grado";
      degreeCount.set(degree, (degreeCount.get(degree) || 0) + 1);
    });

    return Array.from(degreeCount.entries())
      .map(([degree, count]) => ({ label: degree, value: count }))
      .sort((a, b) => b.value - a.value);
  }, [ministers]);

  // Build a lookup map: degree name → total active in system
  const systemMap = useMemo(() => {
    const map = new Map<string, number>();
    systemDegreeCounts.forEach((d) => {
      map.set(d.degreeName, d.totalActive);
    });
    return map;
  }, [systemDegreeCounts]);

  // Extra comparison columns (only shown when system data is available)
  const extraColumns = useMemo(() => {
    if (systemDegreeCounts.length === 0) return [];

    const totalInSystem = systemDegreeCounts.reduce((sum, d) => sum + d.totalActive, 0);
    const totalInRequests = data.reduce((sum, d) => sum + d.value, 0);

    return [
      {
        header: "Total Sistema",
        getValue: (label: string) => {
          const total = systemMap.get(label) ?? 0;
          return <span className="tabular-nums">{total.toLocaleString()}</span>;
        },
        getTotalValue: () => (
          <span className="font-semibold tabular-nums">{totalInSystem.toLocaleString()}</span>
        ),
      },
      {
        header: "% Cred.",
        getValue: (label: string, inRequests: number) => {
          const total = systemMap.get(label) ?? 0;
          if (total === 0) return <span className="text-muted-foreground">—</span>;
          const pct = (inRequests / total) * 100;
          return (
            <div className="flex items-center justify-end gap-1.5">
              <div className="h-1.5 w-8 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
              <span className="w-9 text-right text-xs tabular-nums">
                {pct.toFixed(1)}%
              </span>
            </div>
          );
        },
        getTotalValue: () => {
          if (totalInSystem === 0) return <span className="text-muted-foreground">—</span>;
          const pct = (totalInRequests / totalInSystem) * 100;
          return (
            <div className="flex items-center justify-end gap-1.5">
              <div className="h-1.5 w-8 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
              <span className="w-9 text-right text-xs font-semibold tabular-nums">
                {pct.toFixed(1)}%
              </span>
            </div>
          );
        },
      },
    ];
  }, [systemDegreeCounts, systemMap, data]);

  return (
    <InteractivePieChartCard
      layout="wide-table"
      title="Distribución por Grado"
      subtitle={
        systemDegreeCounts.length > 0
          ? "En solicitudes vs total activos en el sistema"
          : "Haz clic en las filas para mostrar/ocultar segmentos"
      }
      data={data}
      isLoading={isLoading}
      seriesName="Grado"
      tableHeaders={{
        label: "Grado",
        value: "En Solicitudes",
        percentage: "Porcentaje",
      }}
      extraColumns={extraColumns}
    />
  );
}
